/* istanbul ignore file */

import { useCallback } from 'react';
import { type Href, useLocalSearchParams, useNavigation } from 'expo-router';

import { useCart } from '../../../../hooks';
import { useConflictReview } from '../../../../screens/ReorderingScreen';
import { useNavigateToMenuFromLocation } from '../useNavigateToMenuFromLocation';

import type { LocationSearchMachineContext } from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Provides navigation actions and helpers for Location Search.
 */
export const useLocationSearchNavigation = (params: Params = {}) => {
  const { isInsideModal } = params;

  const { cart, isCartEmpty } = useCart();
  const conflictReview = useConflictReview();

  // ─── Navigation ──────────────────────────────────────────────────────

  const navigation = useNavigation();
  const routeParams = useLocalSearchParams<{ backToPath?: string; category?: string }>();

  const navigateToMenu = useNavigateToMenuFromLocation({
    backToPath: routeParams.backToPath as Href | undefined,
    isInsideModal,
  });

  const category = routeParams.category;

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Initiates a navigation event for pickup and outpost locations
   * based on the active location context.
   */
  const navigateToLocation = useCallback(
    (context: LocationSearchMachineContext) => {
      const { locationSearchType: orderType, activeLocation } = context;
      const cartRestaurantSlug = cart?.restaurant?.slug;

      if (!activeLocation) return;

      const { restaurantName, restaurantId, restaurantSlug, isOutpost } = activeLocation;

      // ─── Possible Reorder ────────────────────────────────────────────────

      const shouldStartReorder = !isCartEmpty && cartRestaurantSlug !== restaurantSlug;

      // if your bag belongs to a different restaurant, start reorder flow
      if (shouldStartReorder) {
        void conflictReview({
          orderId: cart?.id,
          orderType: isOutpost ? 'outpost' : 'pickup',
          restaurantId,
          restaurantName,
          restaurantSlug,
        });

        return;
      }

      // ─────────────────────────────────────────────────────────────────────

      navigateToMenu({
        // @ts-expect-error TS(2322): LocationsSearchType | undefined not assignable to AddressType
        orderType,
        restaurantId,
        restaurantName,
        restaurantSlug,
        category,
      });
    },
    [cart?.id, cart?.restaurant?.slug, category, conflictReview, isCartEmpty, navigateToMenu],
  );

  /**
   * Initiates a navigation event for delivery location
   * based on the active location context.
   */
  const navigateToDeliveryLocation = useCallback(
    (context: LocationSearchMachineContext) => {
      const { deliveryLocation } = context;

      if (!deliveryLocation) return;

      const {
        addressId,
        restaurantId,
        restaurantName,
        restaurantSlug,
        restaurantDeliveryFee = 0,
        name: deliveryAddressName,
        deliveryVendorName,
        deliveryVendorRestaurantId,
      } = deliveryLocation;

      navigateToMenu({
        orderType: 'delivery',
        restaurantId,
        restaurantName,
        restaurantSlug,
        deliveryAddressName,
        deliveryOrderDetail: {
          addressId,
          tip: 0,
          deliveryFee: restaurantDeliveryFee,
          vendor: deliveryVendorName,
          vendorRestaurantId: deliveryVendorRestaurantId,
        },
        category,
      });
    },
    [category, navigateToMenu],
  );

  const setOrderChannelParams = useCallback(
    (orderChannel: string | undefined) => {
      navigation.setParams({ orderChannel } as never);
    },
    [navigation],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    navigateToLocation,
    navigateToDeliveryLocation,
    setOrderChannelParams,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  isInsideModal?: boolean;
};
